
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { Navigate, Link } from 'react-router-dom';
import { 
  PenTool, ArrowUpRight, MessageSquare, 
  Trash2, Edit3, Mail, BookOpen, Eye, 
  Save, Briefcase, Linkedin, Twitter, Globe, GraduationCap,
  Camera, Loader2, Fingerprint, BarChart, ThumbsUp, Settings,
  ShieldCheck, Image as ImageIcon, Upload, Star, Plus, X, Server, Database, Activity, DatabaseZap,
  Users, UserCheck, ShieldAlert, ChevronDown, PlusCircle, MinusCircle, CheckCircle, Clock, Key, Download,
  BadgeCheck
} from 'lucide-react';
import { Permission, Testimonial, BlogPost } from '../types';
import { db } from '../db';

const Profile: React.FC = () => {
  const { 
    currentUser, registeredUsers, posts, comments, customRoles, testimonials,
    deletePost, updatePostMetrics, hasPermission, updateProfile,
    addTestimonial, updateTestimonial, deleteTestimonial, resetToDefaults,
    updateUserRole, toggleUserVerification, deleteUser, approvePost
  } = useApp();
  
  const [activeTab, setActiveTab] = useState<'posts' | 'moderation' | 'management' | 'metrics' | 'identity' | 'testimonials' | 'diagnostics'>('posts');
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const caLogoInputRef = useRef<HTMLInputElement>(null);
  
  const [bio, setBio] = useState(currentUser?.bio || '');
  const [designation, setDesignation] = useState(currentUser?.designation || '');
  const [avatar, setAvatar] = useState(currentUser?.avatar || '');
  const [caLogo, setCaLogo] = useState(currentUser?.caLogo || '');
  const [location, setLocation] = useState(currentUser?.location || '');
  const [yearsExperience, setYearsExperience] = useState(currentUser?.yearsExperience || 0);
  const [specializations, setSpecializations] = useState(currentUser?.specializations?.join(', ') || '');
  const [education, setEducation] = useState(currentUser?.education?.join(', ') || '');
  const [achievements, setAchievements] = useState(currentUser?.achievements?.join(', ') || '');
  const [philosophyTitle, setPhilosophyTitle] = useState(currentUser?.philosophyTitle || '');
  const [philosophyText, setPhilosophyText] = useState(currentUser?.philosophyText || '');
  
  const [linkedinUrl, setLinkedinUrl] = useState(currentUser?.linkedinUrl || '');
  const [twitterUrl, setTwitterUrl] = useState(currentUser?.twitterUrl || '');
  const [websiteUrl, setWebsiteUrl] = useState(currentUser?.websiteUrl || '');

  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [isUpdatingCALogo, setIsUpdatingCALogo] = useState(false);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Testimonial Form State
  const [isAddingTestimonial, setIsAddingTestimonial] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<string | null>(null);
  const [testimonialForm, setTestimonialForm] = useState<Omit<Testimonial, 'id'>>({ name: '', role: '', content: '' });

  useEffect(() => {
    const fetchDiag = async () => {
      const diag = await db.getDiagnostics();
      setDiagnostics(diag);
    };
    if (activeTab === 'diagnostics') fetchDiag();
  }, [activeTab]);

  if (!currentUser) return <Navigate to="/" replace />;

  const myPosts = posts.filter(p => p.authorId === currentUser.id);
  const pendingPosts = posts.filter(p => p.status === 'pending');

  const handleIdentitySave = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({
      bio, designation, avatar, caLogo, location,
      yearsExperience: Number(yearsExperience),
      specializations: specializations.split(',').map(s => s.trim()).filter(s => s !== ''),
      education: education.split(',').map(s => s.trim()).filter(s => s !== ''),
      achievements: achievements.split(',').map(s => s.trim()).filter(s => s !== ''),
      philosophyTitle, philosophyText,
      linkedinUrl, twitterUrl, websiteUrl
    });
    alert('Institutional Persona Deployed Successfully');
  };

  const handleExportSQL = async () => {
    setIsExporting(true);
    const sql = await db.generateMigrationScript();
    const blob = new Blob([sql], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ca_manthan_deployment_${Date.now()}.sql`;
    a.click();
    setIsExporting(false);
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUpdatingAvatar(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setAvatar(base64);
        updateProfile({ avatar: base64 });
        setIsUpdatingAvatar(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCALogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUpdatingCALogo(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setCaLogo(base64);
        updateProfile({ caLogo: base64 });
        setIsUpdatingCALogo(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTestimonialSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTestimonial) {
      updateTestimonial(editingTestimonial, testimonialForm);
      setEditingTestimonial(null);
    } else {
      addTestimonial(testimonialForm);
      setIsAddingTestimonial(false);
    }
    setTestimonialForm({ name: '', role: '', content: '' });
  };

  const startEditTestimonial = (t: Testimonial) => {
    setEditingTestimonial(t.id);
    setTestimonialForm({ name: t.name, role: t.role, content: t.content });
    setIsAddingTestimonial(true);
  };

  const handleMetricChange = (postId: string, field: 'views' | 'likes', amount: number) => {
    const post = posts.find(p => p.id === postId);
    if (post) {
      const newValue = Math.max(0, (post[field] as number) + amount);
      updatePostMetrics(postId, { [field]: newValue });
    }
  };

  const handleMetricInput = (postId: string, field: 'views' | 'likes', val: string) => {
    const num = parseInt(val) || 0;
    updatePostMetrics(postId, { [field]: Math.max(0, num) });
  };

  return (
    <div className="pt-40 pb-32 min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Profile Header */}
        <header className="mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row items-center gap-10 p-10 md:p-14 rounded-[3.5rem] bg-white border border-slate-200 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-50 blur-[120px] rounded-full pointer-events-none" />
            
            <div className="relative group shrink-0">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-[2.5rem] bg-slate-50 border-2 border-slate-200 flex items-center justify-center text-indigo-600 text-5xl md:text-6xl font-black shadow-xl overflow-hidden relative">
                {avatar ? <img src={avatar} className="w-full h-full object-cover" alt="Avatar" /> : currentUser.name.charAt(0)}
                {isUpdatingAvatar && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>}
              </div>
              <button 
                onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 bg-indigo-600/0 hover:bg-indigo-600/20 flex items-center justify-center transition-all opacity-0 hover:opacity-100 cursor-pointer"
              >
                <Camera size={32} className="text-white" />
              </button>
              <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={handleAvatarUpload} />
            </div>
            
            <div className="text-center md:text-left flex-grow">
              <div className="flex flex-col md:flex-row items-center gap-4 mb-2">
                <h1 className="text-3xl md:text-5xl font-display font-black text-slate-900 tracking-tight">{currentUser.name}</h1>
                {currentUser.isVerifiedMember && (
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full text-blue-600 text-[10px] font-black uppercase tracking-widest">
                    <BadgeCheck size={14} className="fill-blue-600 text-white" /> Verified Member
                  </span>
                )}
              </div>
              <p className="text-indigo-600 text-sm font-black uppercase tracking-widest mb-6">{designation || 'Institutional Architect'}</p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                <Link to={`/author/${currentUser.id}`} className="inline-flex items-center gap-2 px-6 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-900 hover:border-slate-300 transition-all text-xs font-bold uppercase tracking-widest">
                  View Public Persona <ArrowUpRight size={14} />
                </Link>
                {currentUser.isAdmin && (
                   <span className="px-4 py-2 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                     <ShieldCheck size={14} /> Root Access Authorized
                   </span>
                )}
              </div>
            </div>

            <Link to="/create-post" className="p-6 bg-indigo-600 text-white rounded-3xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/20 group">
              <PenTool size={32} className="group-hover:rotate-12 transition-transform" />
            </Link>
          </motion.div>
        </header>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-12 p-1.5 bg-white rounded-[2rem] w-fit border border-slate-200 shadow-lg">
          {[
            { id: 'posts', label: 'Drafts & Insights', icon: BookOpen },
            { id: 'identity', label: 'Identity Architect', icon: Briefcase },
            { id: 'moderation', label: 'Moderation Queue', icon: ShieldAlert, perm: 'CAN_APPROVE_POSTS', count: pendingPosts.length },
            { id: 'management', label: 'User Directory', icon: Users, perm: 'CAN_MANAGE_USERS' },
            { id: 'metrics', label: 'Engagement Data', icon: BarChart, perm: 'CAN_TUNE_METRICS' },
            { id: 'testimonials', label: 'Client Success', icon: Star, perm: 'CAN_MANAGE_TESTIMONIALS' },
            { id: 'diagnostics', label: 'Engine Diagnostics', icon: Database, perm: 'CAN_APPROVE_POSTS' }
          ].map(tab => (
            (!tab.perm || hasPermission(tab.perm as Permission)) && (
              <button 
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)} 
                className={`px-6 py-3 rounded-2xl font-bold text-xs transition-all flex items-center gap-2.5 relative ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
              >
                <tab.icon size={16} /> 
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black flex items-center justify-center rounded-full border-2 border-white animate-pulse">
                    {tab.count}
                  </span>
                )}
              </button>
            )
          ))}
        </div>

        <section>
          <AnimatePresence mode="wait">
            {activeTab === 'diagnostics' && diagnostics && (
              <motion.div key="diagnostics" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-8 rounded-[2.5rem] bg-white border border-slate-200 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                        <Activity size={24} />
                      </div>
                      <h4 className="text-slate-900 font-bold uppercase tracking-widest text-xs">Engine Health</h4>
                    </div>
                    <p className="text-3xl font-display font-black text-slate-900">{diagnostics.status}</p>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-2">Protocol: {diagnostics.engine}</p>
                  </div>

                  <div className="p-8 rounded-[2.5rem] bg-white border border-slate-200 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                        <DatabaseZap size={24} />
                      </div>
                      <h4 className="text-slate-900 font-bold uppercase tracking-widest text-xs">Record Volume</h4>
                    </div>
                    <p className="text-3xl font-display font-black text-slate-900">{diagnostics.records}</p>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-2">Utilization: {diagnostics.utilization}</p>
                  </div>

                  <div className="p-8 rounded-[2.5rem] bg-white border border-slate-200 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                        <Server size={24} />
                      </div>
                      <h4 className="text-slate-900 font-bold uppercase tracking-widest text-xs">Query Latency</h4>
                    </div>
                    <p className="text-3xl font-display font-black text-slate-900">{diagnostics.loadTime}</p>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-2">Target: &lt; 500ms</p>
                  </div>
                </div>

                {/* Secure Credentials Vault */}
                <div className="p-10 rounded-[3rem] bg-indigo-50 border border-indigo-100 shadow-xl overflow-hidden relative">
                  <div className="absolute top-0 right-0 p-12 text-indigo-200 pointer-events-none">
                    <Key size={160} />
                  </div>
                  <h3 className="text-xl font-display font-bold text-slate-900 mb-6 uppercase tracking-widest flex items-center gap-3">
                    <Key size={20} className="text-indigo-600" /> Root Credentials Vault
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                    <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Institutional Email</p>
                      <p className="text-slate-900 font-mono font-bold">manthan@ca.com</p>
                    </div>
                    <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Protocol Access Key</p>
                      <p className="text-slate-900 font-mono font-bold">admin123</p>
                    </div>
                  </div>
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-6">
                    Note: These credentials are hard-coded in the <code className="text-indigo-600">schema.sql</code> seed for immediate post-deployment access.
                  </p>
                </div>

                {/* MySQL Deployment Engine */}
                <div className="p-10 rounded-[3rem] bg-white border border-slate-200 shadow-xl">
                   <div className="flex flex-col md:flex-row items-center justify-between gap-10">
                      <div className="flex items-center gap-6">
                        <div className="w-16 h-16 rounded-3xl bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100">
                          <Download size={32} />
                        </div>
                        <div>
                          <h4 className="text-slate-900 font-bold text-lg">MySQL Migration Protocol</h4>
                          <p className="text-slate-500 text-sm">Generate a production-ready SQL script to synchronize your current frontend state to a live institutional MySQL instance.</p>
                        </div>
                      </div>
                      <button 
                        onClick={handleExportSQL}
                        disabled={isExporting}
                        className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl text-[10px] uppercase tracking-widest transition-all shadow-xl shadow-indigo-600/20 flex items-center gap-2"
                      >
                        {isExporting ? <Loader2 className="animate-spin" /> : <Download size={14} />} 
                        Download Deployment SQL
                      </button>
                   </div>
                </div>

                <div className="p-10 rounded-[3rem] bg-red-50 border border-red-100 flex items-center justify-between gap-10">
                  <div className="flex items-center gap-6">
                    <div className="w-16 h-16 rounded-3xl bg-white border border-red-100 flex items-center justify-center text-red-500 shadow-sm">
                      <ShieldCheck size={32} />
                    </div>
                    <div>
                      <h4 className="text-slate-900 font-bold text-lg">Wipe Institutional Memory</h4>
                      <p className="text-slate-500 text-sm">Hard reset all database nodes to factory defaults. This action is irreversible.</p>
                    </div>
                  </div>
                  <button 
                    onClick={resetToDefaults}
                    className="px-8 py-4 bg-white hover:bg-red-50 text-red-500 hover:text-red-600 font-black rounded-2xl text-[10px] uppercase tracking-widest transition-all border border-red-200 shadow-sm"
                  >
                    Initialize Reset Protocol
                  </button>
                </div>
              </motion.div>
            )}

            {activeTab === 'moderation' && hasPermission('CAN_APPROVE_POSTS') && (
              <motion.div key="moderation" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
                <div className="p-10 bg-white border border-slate-200 rounded-[3.5rem] shadow-2xl">
                   <h3 className="text-2xl font-display font-bold text-slate-900 mb-2 flex items-center gap-3">
                     <ShieldAlert className="text-red-500" /> Pending Intelligence Audit
                   </h3>
                   <p className="text-slate-500 text-sm mb-10">Review and authorize contributor insights for public dissemination.</p>
                   
                   <div className="grid grid-cols-1 gap-6">
                      {pendingPosts.map((post) => (
                        <div key={post.id} className="p-8 bg-slate-50 border border-slate-200 rounded-[2.5rem] flex flex-col md:flex-row items-center gap-8 group hover:border-indigo-200 transition-all shadow-sm">
                           <div className="w-full md:w-48 aspect-video rounded-2xl overflow-hidden bg-white shrink-0 border border-slate-200">
                              <img src={post.imageUrl} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" alt="Post visual" />
                           </div>
                           <div className="flex-grow">
                              <div className="flex items-center gap-3 mb-3">
                                <span className="px-2.5 py-1 bg-white text-slate-500 text-[9px] font-black uppercase tracking-widest rounded-lg border border-slate-200 shadow-sm">{post.category}</span>
                                <span className="text-[10px] text-slate-500 font-bold uppercase flex items-center gap-1.5"><Clock size={12}/> Submitted {post.date}</span>
                              </div>
                              <h4 className="text-xl font-bold text-slate-900 mb-2">{post.title}</h4>
                              <p className="text-slate-500 text-sm line-clamp-1 mb-4">Author: <span className="text-indigo-600">{post.author}</span></p>
                              <div className="flex flex-wrap gap-3">
                                 <button 
                                   onClick={() => approvePost(post.id)}
                                   className="px-6 py-2.5 bg-green-50 hover:bg-green-100 text-green-600 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 border border-green-200"
                                 >
                                   <CheckCircle size={14} /> Authorize Publication
                                 </button>
                                 <button 
                                   onClick={() => deletePost(post.id)}
                                   className="px-6 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 border border-red-200"
                                 >
                                   <Trash2 size={14} /> Purge Record
                                 </button>
                                 <Link 
                                   to={`/edit-post/${post.id}`}
                                   className="px-6 py-2.5 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-900 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center gap-2 border border-slate-200"
                                 >
                                   <Edit3 size={14} /> Edit for Clarity
                                 </Link>
                              </div>
                           </div>
                        </div>
                      ))}
                      {pendingPosts.length === 0 && (
                        <div className="py-20 text-center bg-slate-50 rounded-[3rem] border border-dashed border-slate-200">
                          <CheckCircle className="mx-auto text-green-500/40 mb-4" size={48} />
                          <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">Audit Queue is Clear.</p>
                        </div>
                      )}
                   </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'management' && hasPermission('CAN_MANAGE_USERS') && (
              <motion.div key="management" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="p-10 bg-white border border-slate-200 rounded-[3.5rem] shadow-2xl">
                  <h3 className="text-2xl font-display font-bold text-slate-900 mb-8 flex items-center gap-3">
                    <UserCheck className="text-indigo-600" /> User Directory & Access Management
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pl-4">Institutional User</th>
                          <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Access Protocol</th>
                          <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Verification</th>
                          <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right pr-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {registeredUsers.map((user) => (
                          <tr key={user.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="py-6 pl-4">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-indigo-600 font-bold shadow-sm overflow-hidden">
                                  {user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : user.name.charAt(0)}
                                </div>
                                <div>
                                  <p className="text-slate-900 font-bold flex items-center gap-2">
                                    {user.name} 
                                    {user.id === currentUser.id && <span className="text-[10px] text-indigo-600 ml-1">(You)</span>}
                                    {user.isVerifiedMember && <BadgeCheck size={14} className="fill-blue-500 text-white" />}
                                  </p>
                                  <p className="text-slate-500 text-xs">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-6">
                              <div className="relative w-40">
                                <select 
                                  value={user.role} 
                                  disabled={user.id === currentUser.id}
                                  onChange={(e) => updateUserRole(user.id, e.target.value)}
                                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 focus:border-indigo-500 outline-none appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                >
                                  <option value="admin">Administrator</option>
                                  <option value="marketer">Digital Marketer</option>
                                  <option value="guest">Guest Contributor</option>
                                </select>
                                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                              </div>
                            </td>
                            <td className="py-6">
                              {user.id !== currentUser.id && (
                                <button
                                  onClick={() => toggleUserVerification(user.id)}
                                  className={`px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
                                    user.isVerifiedMember 
                                      ? 'bg-blue-50 border-blue-200 text-blue-600' 
                                      : 'bg-white border-slate-200 text-slate-400 hover:border-blue-200 hover:text-blue-500'
                                  }`}
                                >
                                  <BadgeCheck size={14} className={user.isVerifiedMember ? "fill-blue-600 text-white" : ""} />
                                  {user.isVerifiedMember ? 'Verified' : 'Verify'}
                                </button>
                              )}
                            </td>
                            <td className="py-6 text-right pr-4">
                              {user.id !== currentUser.id && (
                                <button 
                                  onClick={() => { if(confirm(`Revoke all access for ${user.name}?`)) deleteUser(user.id); }}
                                  className="p-3 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'metrics' && hasPermission('CAN_TUNE_METRICS') && (
              <motion.div key="metrics" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <div key={post.id} className="p-8 rounded-[3rem] bg-white border border-slate-200 hover:border-indigo-200 transition-all flex flex-col h-full shadow-xl relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-8 text-indigo-50 group-hover:text-indigo-100 transition-colors">
                        <BarChart size={120} />
                      </div>
                      
                      <div className="mb-6 relative z-10">
                        <span className="px-3 py-1 bg-indigo-50 border border-indigo-100 text-indigo-600 text-[9px] font-black uppercase tracking-widest rounded-lg mb-4 inline-block">
                          {post.category}
                        </span>
                        <h3 className="text-xl font-display font-bold text-slate-900 line-clamp-1">{post.title}</h3>
                        <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">Author: {post.author}</p>
                      </div>

                      <div className="mt-auto space-y-6 relative z-10">
                        {/* Views Editor */}
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Eye size={12} /> Institutional Views
                          </label>
                          <div className="flex items-center gap-3">
                            <button 
                              onClick={() => handleMetricChange(post.id, 'views', -10)}
                              className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-900 transition-colors"
                            >
                              <MinusCircle size={16} />
                            </button>
                            <input 
                              type="number"
                              value={post.views}
                              onChange={(e) => handleMetricInput(post.id, 'views', e.target.value)}
                              className="flex-grow bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-center text-slate-900 font-black text-sm outline-none focus:border-indigo-500 focus:bg-white"
                            />
                            <button 
                              onClick={() => handleMetricChange(post.id, 'views', 10)}
                              className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-900 transition-colors"
                            >
                              <PlusCircle size={16} />
                            </button>
                          </div>
                        </div>

                        {/* Likes Editor */}
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <ThumbsUp size={12} /> Engagement (Likes)
                          </label>
                          <div className="flex items-center gap-3">
                            <button 
                              onClick={() => handleMetricChange(post.id, 'likes', -1)}
                              className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-900 transition-colors"
                            >
                              <MinusCircle size={16} />
                            </button>
                            <input 
                              type="number"
                              value={post.likes}
                              onChange={(e) => handleMetricInput(post.id, 'likes', e.target.value)}
                              className="flex-grow bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-center text-slate-900 font-black text-sm outline-none focus:border-indigo-500 focus:bg-white"
                            />
                            <button 
                              onClick={() => handleMetricChange(post.id, 'likes', 1)}
                              className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-900 transition-colors"
                            >
                              <PlusCircle size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'identity' && (
              <motion.div key="identity" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl space-y-10">
                <form onSubmit={handleIdentitySave} className="space-y-10">
                  {/* Institutional Branding - ADMIN ONLY */}
                  {currentUser.isAdmin && (
                    <div className="p-10 bg-indigo-50 border border-indigo-100 rounded-[3.5rem] shadow-sm relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-12 text-indigo-100 pointer-events-none group-hover:text-indigo-200 transition-colors">
                        <ShieldCheck size={200} />
                      </div>
                      <h3 className="text-xl font-display font-bold text-slate-900 mb-8 uppercase tracking-widest flex items-center gap-2">
                        <ShieldCheck size={20} className="text-indigo-600" /> Institutional Verification Seal
                      </h3>
                      <div className="flex flex-col md:flex-row items-center gap-10">
                         <div className="w-32 h-32 rounded-3xl bg-white p-4 shadow-xl relative flex items-center justify-center shrink-0 border border-slate-100">
                            {caLogo ? <img src={caLogo} className="w-full h-full object-contain" alt="CA Logo" /> : <ImageIcon size={32} className="text-slate-200" />}
                            {isUpdatingCALogo && <div className="absolute inset-0 bg-white/80 flex items-center justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>}
                         </div>
                         <div className="flex-grow space-y-4">
                            <p className="text-slate-500 text-sm leading-relaxed max-w-md">
                              This logo represents your institutional status as a Chartered Accountant. It will be displayed on all your analysis and profile touchpoints across the platform.
                            </p>
                            <button 
                              type="button"
                              onClick={() => caLogoInputRef.current?.click()}
                              className="px-6 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm hover:bg-slate-50"
                            >
                              <Upload size={14} /> Replace Institutional Seal
                            </button>
                            <input type="file" ref={caLogoInputRef} className="hidden" accept="image/*" onChange={handleCALogoUpload} />
                         </div>
                      </div>
                    </div>
                  )}

                  <div className="p-10 bg-white border border-slate-200 rounded-[3rem] shadow-sm">
                    <h3 className="text-xl font-display font-bold text-slate-900 mb-8 uppercase tracking-widest flex items-center gap-2">
                      <Fingerprint size={20} className="text-indigo-600" /> Professional Anchors
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Academic Designation</label>
                        <input type="text" value={designation} onChange={(e) => setDesignation(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 focus:border-indigo-500 outline-none focus:bg-white" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Global Location</label>
                        <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 focus:border-indigo-500 outline-none focus:bg-white" />
                      </div>
                    </div>
                  </div>

                  <div className="p-10 bg-white border border-slate-200 rounded-[3rem] shadow-sm">
                    <h3 className="text-xl font-display font-bold text-slate-900 mb-8 uppercase tracking-widest flex items-center gap-2">
                      <Globe size={20} className="text-indigo-600" /> Institutional Connectivity
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Linkedin size={10} /> LinkedIn Protocol</label>
                        <input type="url" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder="https://linkedin.com/in/..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-600 focus:border-indigo-500 outline-none text-xs focus:bg-white" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Twitter size={10} /> Twitter Endpoint</label>
                        <input type="url" value={twitterUrl} onChange={(e) => setTwitterUrl(e.target.value)} placeholder="https://twitter.com/..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-600 focus:border-indigo-500 outline-none text-xs focus:bg-white" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Globe size={10} /> Sovereign Website</label>
                        <input type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-600 focus:border-indigo-500 outline-none text-xs focus:bg-white" />
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="w-full py-6 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl shadow-indigo-600/20 transition-all uppercase tracking-[0.2em] text-sm">
                    <Save size={20} className="inline mr-2" /> Sync Institutional Identity
                  </button>
                </form>
              </motion.div>
            )}

            {activeTab === 'posts' && (
              <motion.div key="posts" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {myPosts.map((post) => (
                  <div key={post.id} className="p-8 rounded-[3rem] bg-white border border-slate-200 hover:border-indigo-200 transition-all flex flex-col h-full shadow-lg relative">
                    <div className="mb-4 flex items-center justify-between">
                       <span className={`px-2 py-1 text-[8px] font-black uppercase tracking-widest rounded-lg border ${post.status === 'published' ? 'bg-green-50 text-green-600 border-green-200' : 'bg-yellow-50 text-yellow-600 border-yellow-200'}`}>
                         {post.status}
                       </span>
                    </div>
                    <h3 className="text-xl font-display font-bold text-slate-900 mb-6 line-clamp-2">{post.title}</h3>
                    <div className="mt-auto pt-8 border-t border-slate-100 flex items-center justify-between">
                      <Link to={`/edit-post/${post.id}`} className="text-indigo-600 font-bold text-xs uppercase tracking-widest hover:text-indigo-800">Edit Intelligence</Link>
                      <button onClick={() => deletePost(post.id)} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}
                {myPosts.length === 0 && (
                  <div className="col-span-full py-20 text-center bg-slate-50 rounded-[3rem] border border-dashed border-slate-200">
                    <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">No insights drafted.</p>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'testimonials' && hasPermission('CAN_MANAGE_TESTIMONIALS') && (
              <motion.div key="testimonials" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
                <div className="flex justify-between items-center">
                  <h2 className="text-3xl font-display font-black text-slate-900">Client Success Registry</h2>
                  <button 
                    onClick={() => { setIsAddingTestimonial(true); setEditingTestimonial(null); setTestimonialForm({ name: '', role: '', content: '' }); }}
                    className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl flex items-center gap-2 hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20"
                  >
                    <Plus size={18} /> Deploy New Success Record
                  </button>
                </div>

                {isAddingTestimonial && (
                  <motion.form 
                    initial={{ opacity: 0, y: -20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    onSubmit={handleTestimonialSubmit} 
                    className="p-10 bg-white border border-indigo-100 rounded-[3rem] space-y-6 relative shadow-2xl"
                  >
                    <button 
                      type="button" 
                      onClick={() => setIsAddingTestimonial(false)}
                      className="absolute top-8 right-8 text-slate-400 hover:text-slate-900"
                    >
                      <X size={24} />
                    </button>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">{editingTestimonial ? 'Update Record' : 'Initialize Record'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Principal Name</label>
                        <input 
                          type="text" required value={testimonialForm.name} 
                          onChange={e => setTestimonialForm({...testimonialForm, name: e.target.value})} 
                          className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 outline-none focus:border-indigo-500 focus:bg-white" 
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Institutional Role</label>
                        <input 
                          type="text" required value={testimonialForm.role} 
                          onChange={e => setTestimonialForm({...testimonialForm, role: e.target.value})} 
                          className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 outline-none focus:border-indigo-500 focus:bg-white" 
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Institutional Discourse</label>
                      <textarea 
                        required value={testimonialForm.content} 
                        onChange={e => setTestimonialForm({...testimonialForm, content: e.target.value})} 
                        rows={4}
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 outline-none focus:border-indigo-500 resize-none focus:bg-white" 
                      />
                    </div>
                    <button type="submit" className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-xl transition-all">
                      {editingTestimonial ? 'Sync Updates' : 'Commit Success Record'}
                    </button>
                  </motion.form>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {testimonials.map((t) => (
                    <div key={t.id} className="p-8 bg-white border border-slate-200 rounded-[2.5rem] flex flex-col hover:border-indigo-200 transition-all shadow-sm">
                      <p className="text-slate-600 italic mb-8 leading-relaxed font-medium">"{t.content}"</p>
                      <div className="mt-auto pt-8 border-t border-slate-100 flex justify-between items-center">
                        <div>
                          <h4 className="text-slate-900 font-bold text-sm">{t.name}</h4>
                          <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-0.5">{t.role}</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => startEditTestimonial(t)} className="p-3 bg-slate-50 hover:bg-indigo-50 text-slate-500 hover:text-indigo-600 rounded-xl transition-all"><Edit3 size={16} /></button>
                          <button onClick={() => deleteTestimonial(t.id)} className="p-3 bg-slate-50 hover:bg-red-50 text-slate-500 hover:text-red-500 rounded-xl transition-all"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {testimonials.length === 0 && !isAddingTestimonial && (
                    <div className="col-span-full py-20 text-center bg-slate-50 rounded-[3rem] border border-dashed border-slate-200">
                      <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">No success records deployed.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      </div>
    </div>
  );
};

export default Profile;
